import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import {
  mapApprovalTimeChart,
  mapComplaintsChart,
  mapOnHoldCanceledChart,
  mapOperationsExecutionTrend,
  mapTotalRequestsTrendChart,
} from "@/components/pages/analytics/front-desk/components/operations/operationsTabMappers";
import type { OperationsSummary } from "@/components/pages/analytics/front-desk/components/operations/types";
import { analyticsOperationsService } from "@/services/analytics-operations-service";
import { analyticsTenantService } from "@/services/analytics-tenant-service";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import type { AdminHotelMetricRow } from "../types/adminHotelMetricTable";
import {
  applyTenantOperationsKpiDeltas,
  mapOperationsHotelRows,
  mapTenantOperationsSummary,
  type HotelOperationsSnapshot,
} from "../utils/adminOperationsMappers";
import { useAdminTenantId } from "./useAdminTenantId";

function isDateRangeReady(dateRange: DateRange): boolean {
  return Boolean(
    toAnalyticsDateParam(dateRange.from) && toAnalyticsDateParam(dateRange.to),
  );
}

async function fetchHotelOperationsSnapshot(
  hotelId: string,
  from: string,
  to: string,
): Promise<HotelOperationsSnapshot | null> {
  try {
    const [totalRequests, processingTime, onTimeRate, complaints] =
      await Promise.all([
        analyticsOperationsService.getTotalRequests({ hotelId, from, to }),
        analyticsOperationsService.getProcessingTime({ hotelId, from, to }),
        analyticsOperationsService.getOnTimeRate({ hotelId, from, to }),
        analyticsOperationsService.getComplaints({ hotelId, from, to }),
      ]);

    return { totalRequests, processingTime, onTimeRate, complaints };
  } catch {
    return null;
  }
}

async function fetchAdminOperationsData(
  tenantId: string,
  from: string,
  to: string,
  previousFrom: string,
  previousTo: string,
) {
  const rangeQuery = { from, to };
  const prevRangeQuery = { from: previousFrom, to: previousTo };

  const operationsTable =
    await analyticsTenantService.getOperationsTable(rangeQuery);
  const hotels = operationsTable.rows;

  const [
    totalRequests,
    previousTotalRequests,
    processingTime,
    previousProcessingTime,
    onTimeRate,
    previousOnTimeRate,
    complaints,
    previousComplaints,
    onHoldCanceled,
    approvalTime,
    currentHotelSnapshots,
    previousHotelSnapshots,
    hotelOptions,
  ] = await Promise.all([
    analyticsTenantService.getOperationsTotalRequests(rangeQuery),
    analyticsTenantService.getOperationsTotalRequests(prevRangeQuery),
    analyticsTenantService.getOperationsProcessingTime(rangeQuery),
    analyticsTenantService.getOperationsProcessingTime(prevRangeQuery),
    analyticsTenantService.getOperationsOnTimeRate(rangeQuery),
    analyticsTenantService.getOperationsOnTimeRate(prevRangeQuery),
    analyticsTenantService.getOperationsComplaints(rangeQuery),
    analyticsTenantService.getOperationsComplaints(prevRangeQuery),
    analyticsTenantService.getOperationsOnHoldCanceled(rangeQuery),
    analyticsTenantService.getOperationsApprovalTime(rangeQuery),
    Promise.all(
      hotels.map((hotel) =>
        fetchHotelOperationsSnapshot(hotel.hotelId, from, to),
      ),
    ),
    Promise.all(
      hotels.map((hotel) =>
        fetchHotelOperationsSnapshot(hotel.hotelId, previousFrom, previousTo),
      ),
    ),
    partnerDashboardService
      .getHotelFilterOptions(tenantId)
      .catch(
        () =>
          [] as Awaited<
            ReturnType<typeof partnerDashboardService.getHotelFilterOptions>
          >,
      ),
  ]);

  const hotelLogosById = Object.fromEntries(
    hotelOptions.map((hotel) => [hotel.id, hotel.logoId]),
  );

  const currentByHotelId = new Map(
    hotels.map((hotel, index) => [hotel.hotelId, currentHotelSnapshots[index]]),
  );
  const previousByHotelId = new Map(
    hotels.map((hotel, index) => [
      hotel.hotelId,
      previousHotelSnapshots[index],
    ]),
  );

  const currentSummary = mapTenantOperationsSummary(
    totalRequests,
    processingTime,
    onTimeRate,
    complaints,
  );
  const previousSummary = mapTenantOperationsSummary(
    previousTotalRequests,
    previousProcessingTime,
    previousOnTimeRate,
    previousComplaints,
  );

  return {
    summary: applyTenantOperationsKpiDeltas(currentSummary, previousSummary),
    tableRows: mapOperationsHotelRows(
      hotels,
      currentByHotelId,
      previousByHotelId,
      hotelLogosById,
    ),
    totalRequests,
    processingTime,
    onTimeRate,
    complaints,
    onHoldCanceled,
    approvalTime,
  };
}

export const adminOperationsKeys = {
  all: ["admin-analytics-operations"] as const,
  hotels: (tenantId: string, from: string, to: string) =>
    [...adminOperationsKeys.all, tenantId, from, to] as const,
};

export function useAdminOperationsHotels(dateRange: DateRange) {
  const tenantId = useAdminTenantId();
  const from = toAnalyticsDateParam(dateRange.from) ?? "";
  const to = toAnalyticsDateParam(dateRange.to) ?? "";
  const previousRange = getPreviousAnalyticsDateRange(dateRange);
  const previousFrom = toAnalyticsDateParam(previousRange?.from) ?? "";
  const previousTo = toAnalyticsDateParam(previousRange?.to) ?? "";
  const isReady = Boolean(
    tenantId && isDateRangeReady(dateRange) && previousFrom && previousTo,
  );

  const query = useQuery({
    queryKey: adminOperationsKeys.hotels(tenantId ?? "", from, to),
    queryFn: () =>
      fetchAdminOperationsData(tenantId!, from, to, previousFrom, previousTo),
    enabled: isReady,
  });

  const summary = useMemo<OperationsSummary>(
    () =>
      query.data?.summary ?? {
        totalRequests: 0,
        avgProcessingTime: "—",
        onTimePercent: null,
        complaintCount: 0,
      },
    [query.data?.summary],
  );

  const tableRows = useMemo<AdminHotelMetricRow[]>(
    () => query.data?.tableRows ?? [],
    [query.data?.tableRows],
  );

  const executionTrend = useMemo(
    () =>
      mapOperationsExecutionTrend(
        query.data?.onTimeRate,
        query.data?.processingTime,
      ),
    [query.data?.onTimeRate, query.data?.processingTime],
  );

  const totalRequestsTrendChart = useMemo(
    () => mapTotalRequestsTrendChart(query.data?.totalRequests),
    [query.data?.totalRequests],
  );

  const onHoldCanceledChart = useMemo(
    () => mapOnHoldCanceledChart(query.data?.onHoldCanceled),
    [query.data?.onHoldCanceled],
  );

  const complaintsChart = useMemo(
    () => mapComplaintsChart(query.data?.complaints),
    [query.data?.complaints],
  );

  const approvalTimeChart = useMemo(
    () => mapApprovalTimeChart(query.data?.approvalTime),
    [query.data?.approvalTime],
  );

  return {
    tenantId,
    summary,
    tableRows,
    executionTrend,
    totalRequestsTrendChart,
    onHoldCanceledChart,
    approvalTimeChart,
    complaintsChart,
    isLoading: query.isLoading,
    isError: query.isError,
    isReady,
  };
}
